import dayjs, { Dayjs } from 'dayjs';
import { TFunction } from 'i18next';
import { useCallback, useRef } from 'react';
import ReactDOMServer from 'react-dom/server';
import { useTranslation } from 'react-i18next';

import { PrintablePlanning } from 'src/Components/PrintTemplates/PrintablePlanning';
import type {
  PrintablePlanningView,
  PrintFormat,
  PrintOptions,
} from 'src/Components/PrintTemplates/PrintablePlanning/PrintablePlanning.types';
import { PrintableStaysTimeline } from 'src/Components/PrintTemplates/PrintableStaysTimeline';
import { LocalEvent } from 'src/Hooks/Queries/useFetchEvents';
import { EventDtoTypeEnum } from 'src/Services/API/generated';
import { trackMixpanelEvent } from 'src/Services/Mixpanel';

interface PrintPlanningOptions {
  events: LocalEvent[];
  selectedDate: Dayjs;
  view: PrintablePlanningView;
  assetName: string;
  exportUrl: string;
  activeEventTypes: EventDtoTypeEnum[];
  printOptions: PrintOptions;
  format: PrintFormat;
}

const getRangeForView = (date: Dayjs, view: PrintablePlanningView, locale: string) => {
  if (view === 'week') {
    const startDate = date.locale(locale).startOf('week');
    return { startDate, endDate: startDate.add(6, 'day').endOf('day') };
  }
  const startOfMonth = date.startOf('month');
  const endOfMonth = date.endOf('month');
  const startDate = startOfMonth.locale(locale).startOf('week');
  const endDate = endOfMonth.locale(locale).endOf('week');
  return { startDate, endDate };
};

const formatDateRangeLabel = (
  startDate: Dayjs,
  endDate: Dayjs,
  view: PrintablePlanningView,
  selectedDate: Dayjs,
  locale: string
) => {
  if (view === 'month') return selectedDate.locale(locale).format('MMMM YYYY');
  const start = startDate.locale(locale);
  const end = endDate.locale(locale);
  if (start.isSame(end, 'month')) return `${start.format('D')} – ${end.format('D MMMM YYYY')}`;
  if (start.isSame(end, 'year')) return `${start.format('D MMM')} – ${end.format('D MMM YYYY')}`;
  return `${start.format('D MMM YYYY')} – ${end.format('D MMM YYYY')}`;
};

const buildWeekDayLabels = (startDate: Dayjs, locale: string) => {
  const start = startDate.locale(locale);
  return Array.from({ length: 7 }, (_, i) => start.add(i, 'day').format('ddd'));
};

const computeTodaySummary = (events: LocalEvent[], t: TFunction) => {
  const today = dayjs().startOf('day');
  const occupants = events.filter(event => {
    if (event.type !== EventDtoTypeEnum.Occupation) return false;
    const end = event.endTime ?? event.startTime;
    return !event.startTime.isAfter(today.endOf('day')) && !end.isBefore(today);
  });
  if (occupants.length === 0) {
    return t('planning.print.todayEmpty', 'Aujourd’hui : personne à la maison');
  }
  const names = Array.from(
    new Set(occupants.map(e => e.household?.name ?? e.name).filter((n): n is string => !!n))
  );
  return t('planning.print.today', 'Aujourd’hui : ') + names.join(', ');
};

const usePrintPlanning = () => {
  const printFrameRef = useRef<HTMLIFrameElement | null>(null);
  const { t, i18n } = useTranslation();

  const printPlanning = useCallback(
    ({
      events,
      selectedDate,
      view,
      assetName,
      exportUrl,
      activeEventTypes,
      printOptions,
      format,
    }: PrintPlanningOptions) => {
      try {
        trackMixpanelEvent('Planning - print', {
          view,
          format,
          assetName,
          eventsCount: events.length,
          activeEventTypes,
          printOptions,
        });

        const locale = i18n.language;
        dayjs.locale(locale);

        const { startDate, endDate } = getRangeForView(selectedDate, view, locale);
        const dateRangeLabel = formatDateRangeLabel(startDate, endDate, view, selectedDate, locale);
        const weekDayLabels = buildWeekDayLabels(startDate, locale);

        const eventTypeLabels = Object.values(EventDtoTypeEnum).reduce(
          (acc, type) => {
            acc[type] = t(`createEventForm.eventTypes.${type}`);
            return acc;
          },
          {} as Record<EventDtoTypeEnum, string>
        );

        const printedOnLabel = t('planning.print.printedOn', {
          defaultValue: 'Imprimé le {{date}}',
          date: dayjs().locale(locale).format('DD/MM/YYYY'),
        });
        const todaySummary = computeTodaySummary(events, t);

        if (printFrameRef.current && document.body.contains(printFrameRef.current)) {
          document.body.removeChild(printFrameRef.current);
          printFrameRef.current = null;
        }

        const iframe = document.createElement('iframe');
        document.body.appendChild(iframe);
        printFrameRef.current = iframe;

        const printWindow = iframe.contentWindow;
        if (!printWindow) throw new Error('Impossible to gain access to print window');

        const content = ReactDOMServer.renderToString(
          format === 'stays' ? (
            <PrintableStaysTimeline
              events={events}
              selectedDate={selectedDate}
              assetName={assetName}
              exportUrl={exportUrl}
              dateRangeLabel={dateRangeLabel}
              printedOnLabel={printedOnLabel}
              todaySummary={todaySummary}
            />
          ) : (
            <PrintablePlanning
              events={events}
              startDate={startDate}
              endDate={endDate}
              view={view}
              assetName={assetName}
              exportUrl={exportUrl}
              dateRangeLabel={dateRangeLabel}
              weekDayLabels={weekDayLabels}
              eventTypeLabels={eventTypeLabels}
              activeEventTypes={activeEventTypes}
              printOptions={printOptions}
              todaySummary={todaySummary}
              printedOnLabel={printedOnLabel}
            />
          )
        );

        const printContent = `
          <!DOCTYPE html>
          <html>
          <head>
            <meta name="viewport" content="width=1123, initial-scale=1">
            <title>${t('planning.printTitle', 'Planning')} — ${assetName}</title>
            <link rel="preconnect" href="https://fonts.googleapis.com">
            <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
            <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap" rel="stylesheet">
            <style>
              * { box-sizing: border-box; }
              html, body { margin: 0; padding: 0; }
              @page { size: A4 landscape; margin: 8mm; }
              @media print {
                html, body {
                  print-color-adjust: exact;
                  -webkit-print-color-adjust: exact;
                }
              }
            </style>
            <script>
              document.addEventListener('DOMContentLoaded', function() {
                if (document.fonts && document.fonts.ready) {
                  document.fonts.ready.then(function() { window.print(); }).catch(function() { window.print(); });
                } else {
                  setTimeout(function() { window.print(); }, 1000);
                }
              });
            </script>
          </head>
          <body>${content}</body>
          </html>
        `;

        printWindow.document.open();
        printWindow.document.write(printContent);
        printWindow.document.close();

        const cleanup = () => {
          setTimeout(() => {
            if (printFrameRef.current && document.body.contains(printFrameRef.current)) {
              document.body.removeChild(printFrameRef.current);
              printFrameRef.current = null;
            }
          }, 2000);
        };

        printWindow.addEventListener('afterprint', cleanup);
        setTimeout(cleanup, 10000);
      } catch (error) {
        console.error('Error while printing planning:', error);
      }
    },
    [i18n.language, t]
  );

  return { printPlanning };
};

export default usePrintPlanning;
